import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from './Button';

const DISMISS_KEY = 'sharedReportPrompt.dismissed'; // session-scoped report_ids

function dismissedSet() {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]')); }
  catch { return new Set(); }
}

export default function SharedReportPromptModal({ onPreview, onResolvedAny }) {
  const { API_URL } = useAuth();
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/reports/pending-shares`, cfg());
        const dismissed = dismissedSet();
        setQueue((res.data || []).filter((s) => !dismissed.has(s.report_id)));
      } catch { /* non-fatal */ }
    })();
  }, [API_URL]);

  const current = queue[idx];

  const advance = useCallback(() => setIdx((i) => i + 1), []);

  const respond = async (decision) => {
    if (!current) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/reports/${current.report_id}/respond`, { decision }, cfg());
      if (onResolvedAny) onResolvedAny();
    } catch { /* non-fatal; leave pending for next login */ }
    setBusy(false);
    advance();
  };

  const decideLater = () => {
    const d = dismissedSet(); d.add(current.report_id);
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...d]));
    advance();
  };

  if (!current) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true"
         aria-label="Shared report" data-testid="share_modal">
      <div className="modal-panel" style={{ maxWidth: 440 }}>
        <h3>{current.sender_name} shared a list with you</h3>
        <p style={{ margin: '8px 0' }}>
          <strong>{current.title}</strong><br />
          {current.athlete_count} athlete{current.athlete_count !== 1 ? 's' : ''} · {current.sport}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          <Button variant="secondary" data-testid="share_modal_preview"
                  onClick={() => onPreview && onPreview(current.code)}>Preview</Button>
          <Button variant="primary" loading={busy} data-testid="share_modal_accept"
                  onClick={() => respond('accept')}>Accept</Button>
          <Button variant="danger" loading={busy} data-testid="share_modal_reject"
                  onClick={() => respond('reject')}>Reject</Button>
          <Button variant="ghost" data-testid="share_modal_decide_later"
                  onClick={decideLater}>Decide later</Button>
        </div>
      </div>
    </div>
  );
}
